import { memo } from "react";
import { withForm } from "../../../hooks/useForm";
import { visualizarGramos } from "../../../lib/utils";
import { TableRow, TableCell } from "../../ui/table";
import type {
	Bloque,
	ConfigReferencia,
	Factor,
} from "../../../@types/Api_Extracted";
import { Button } from "../../ui/button";
import { TreeSelect } from "../../ui/chainSelector";
import { Input } from "../../ui/input";

interface InputRowProps {
	index: number;
	bloques: Bloque[] | undefined;
	factores: Factor[] | undefined;
}
const InputRow = withForm({
	defaultValues: { rows: new Array<ConfigReferencia | null>() },
	props: {} as InputRowProps,
	render: function Render({ form, index, bloques, factores }) {
		const values = mapBloquesYFactores(bloques, factores);

		const setFactor = (e: React.ChangeEvent<HTMLInputElement>) => {
			const id = e.target.value;
			const factor = factores?.find((f) => f.idFactor == id);

			form.setFieldValue(`rows[${index}].factor`, factor ?? null);
			form.setFieldValue(
				`rows[${index}].idFactorMiteco`,
				factor?.tipoFactor === "miteco" ? (factor.idFactor as number) : null,
			);
			form.setFieldValue(
				`rows[${index}].idFactorEmpresa`,
				factor?.tipoFactor === "propio" ? (factor.idFactor as string) : null,
			);
		};

		return (
			<TableRow>
				<TableCell width="30%">
					<form.AppField
						name={`rows[${index}].factor`}
						children={(field) => (
							<TreeSelect
								name={`rows[${index}].factor`}
								onChange={setFactor}
								defaultValue={String(field.state.value?.idFactor ?? "-1")}
								placeholder="Selecciona un factor"
								data={values}
							/>
						)}
					/>
				</TableCell>
				<TableCell width="35%">
					<form.AppField
						name={`rows[${index}].descripcionCorta`}
						children={(field) => (
							<Input
								type="text"
								placeholder="Referencia"
								value={field.state.value ?? ""}
								onChange={(e) => field.setValue(e.target.value)}
							/>
						)}
					/>
				</TableCell>
				<TableCell width="15%">
					<form.AppField
						name={`rows[${index}].factor.unidad`}
						children={(field) => field.state.value}
					/>
				</TableCell>
				<TableCell width="20%">
					<form.AppField
						name={`rows[${index}]`}
						children={(field) => {
							const factor = field.state.value?.factor;
							const conversionKg = factor?.conversion_equivalente_kilos ?? 0;
							return visualizarGramos(conversionKg * 1000);
						}}
					/>
				</TableCell>
				<TableCell width="20%">
					<Button
						onClick={() => {
							form.setFieldValue("rows", (oldValues) => {
								const newValues = [...oldValues];
								newValues[index] = null;
								return newValues;
							});
						}}
						variant="outline"
						size="icon"
						type="button"
					>
						X
					</Button>
				</TableCell>
			</TableRow>
		);
	},
});
export const InputRowMemo = memo(InputRow);

/** Mapea los bloques y factores a una estructura de árbol para el selector */
function mapBloquesYFactores(
	bloques: Bloque[] | undefined,
	factores: Factor[] | undefined,
) {
	if (!bloques || !factores) return [];

	type ExtendedBloque = Bloque & {
		factores: Factor[];
		subBloques: ExtendedBloque[];
	};
	const bloquesIndexados: Record<string, ExtendedBloque> = {};
	bloques.forEach((bloque) => {
		bloquesIndexados[bloque.idBloque] = {
			...bloque,
			factores: [],
			subBloques: [],
		};
	});

	bloques.forEach((bloque) => {
		// Group sub-bloques bajo su padre
		if (bloque.idBloque != 0) {
			if (bloquesIndexados[bloque.idPadre] == null) {
				console.error(bloquesIndexados, bloque);
				throw new Error(
					`El sub-bloque con id ${bloque.idPadre} no existe en los bloques proporcionados.`,
				);
			}
			bloquesIndexados[bloque.idPadre]!.subBloques.push(
				bloquesIndexados[bloque.idBloque]!,
			);
		}

		// bloquesIndexados[bloque.idBloque]!.factores.push({
		// 	idFactor: -1, // Placeholder para factores que no son de este bloque
		// 	nombre: "Personalizado",
		// 	idBloque: bloque.idBloque,
		// 	unidad: "",
		// 	conversion_equivalente_kilos: 0,
		// 	conversion_co2_kg: 0,
		// 	conversion_ch4_gramos: 0,
		// 	conversion_n20_gramos: 0,
		// });
	});

	factores.forEach((factor) => {
		if (bloquesIndexados[factor.idBloque] == null)
			throw new Error(
				`El bloque con id ${factor.idBloque} no existe en los bloques proporcionados.`,
			);

		bloquesIndexados[factor.idBloque]?.factores.push(factor);
	});

	const mapBloque = (bloque: ExtendedBloque): TreeNode => {
		return {
			id: `bloque_${bloque.idBloque}`,
			label: bloque.nombreCorto,
			children: [
				...bloque.subBloques.map(mapBloque),
				...bloque.factores.map((factor) => ({
					id: String(factor.idFactor),
					label: factor.nombre,
				})),
			],
		};
	};
	type TreeNode = {
		id: string;
		label: string;
		children?: TreeNode[];
	};
	const primerBloque = bloquesIndexados[0];
	if (!primerBloque)
		throw new Error("No se encontró el bloque raíz (idBloque 0).");

	return mapBloque(primerBloque).children ?? [];
}
